import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import { useContent } from "@/context/ContentContext";
import { ROYAL_IMG, RoyalLiquidBg } from "@/components/royal/shared";

// Royal Signature — Contact: liquid full-bleed header + concierge details + form + map.
export default function ContactRoyal() {
  const { content } = useContent();
  const s = content?.settings || {};
  const wa = (s.whatsapp || "").replace(/[^\d]/g, "");
  const heroBlur = parseInt(s.royal_hero_blur || "0", 10) || 0;

  return (
    <div className="bg-[#FAF8F5]" data-testid="contact-page">
      <section className="relative flex min-h-[48vh] items-center overflow-hidden bg-[#F1EBE2]" data-testid="contact-hero">
        <RoyalLiquidBg src={s.royal_contact_hero_image || s.royal_journey_image || ROYAL_IMG.clinic} blur={heroBlur} testid="contact-hero-image" />
        <div className="relative mx-auto w-full max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <div className="max-w-xl py-16 lg:py-20">
            <Reveal>
              <p className="eyebrow-royal" data-testid="contact-eyebrow">Begin Your Journey</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-4 font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#0F0F0F] sm:text-5xl lg:text-6xl" data-testid="contact-title">
                Book your consultation
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-sm font-normal leading-relaxed text-[#3A3630] sm:text-base">
                Tell us about your skin goals — our concierge replies within hours to confirm your appointment.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 pb-20 pt-12 sm:px-8 lg:px-12">
        <div className="grid gap-12 lg:grid-cols-5 lg:gap-16">
          <div className="space-y-8 lg:col-span-2">
            <Reveal delay={0.15}>
              <div className="space-y-6" data-testid="contact-details">
                {s.address && (
                  <div className="flex gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F8F3E6]">
                      <MapPin className="h-4.5 w-4.5 text-[#C5A059]" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8580]">Visit Us</p>
                      <p className="mt-1.5 text-sm font-normal leading-relaxed text-[#0F0F0F]">{s.address}</p>
                    </div>
                  </div>
                )}
                {s.phone && (
                  <div className="flex gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F8F3E6]">
                      <Phone className="h-4.5 w-4.5 text-[#C5A059]" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8580]">Call Us</p>
                      <a href={`tel:${s.phone.replace(/[^+\d]/g, "")}`} data-testid="contact-phone-link" className="mt-1.5 block text-sm font-normal text-[#0F0F0F] transition-colors hover:text-[#C5A059]">
                        {s.phone}
                      </a>
                    </div>
                  </div>
                )}
                {s.email && (
                  <div className="flex gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F8F3E6]">
                      <Mail className="h-4.5 w-4.5 text-[#C5A059]" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8580]">Email</p>
                      <a href={`mailto:${s.email}`} data-testid="contact-email-link" className="mt-1.5 block text-sm font-normal text-[#0F0F0F] transition-colors hover:text-[#C5A059]">
                        {s.email}
                      </a>
                    </div>
                  </div>
                )}
                {s.hours && (
                  <div className="flex gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F8F3E6]">
                      <Clock className="h-4.5 w-4.5 text-[#C5A059]" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8580]">Hours</p>
                      <p className="mt-1.5 text-sm font-normal text-[#0F0F0F]">{s.hours}</p>
                    </div>
                  </div>
                )}
              </div>
            </Reveal>
            {wa && (
              <Reveal delay={0.25}>
                <a
                  href={`https://wa.me/${wa}?text=${encodeURIComponent("Hello Royal Skin Clinic — I would like to book a consultation.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="btn-royal-outline px-7 py-3.5 text-[10px]"
                >
                  Chat on WhatsApp
                </a>
              </Reveal>
            )}
            {s.map_embed_url && (s.section_visibility?.contact_map ?? true) && (
              <Reveal delay={0.3}>
                <div className="overflow-hidden rounded-2xl border border-[#E8E2D9]" data-testid="contact-map">
                  <iframe src={s.map_embed_url} title="Royal Skin Clinic location map" className="h-64 w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                </div>
              </Reveal>
            )}
          </div>

          <Reveal delay={0.2} className="lg:col-span-3">
            <div className="royal-card rounded-[2rem] p-6 sm:p-10" data-testid="contact-form-card">
              <p className="eyebrow-royal">Consultation Request</p>
              <h2 className="mt-3 font-serif text-2xl font-normal text-[#0F0F0F] sm:text-3xl">We reply within hours</h2>
              <div className="mt-8">
                <EnquiryForm prefix="contact-enquiry" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
